/**
 * Face detection module — MediaPipe Face Landmarker (Tasks Vision / WASM).
 * Singleton so the heavy WASM model loads once per session.
 */

import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import {
  FACE_LANDMARKER_MODEL_URL,
  MEDIAPIPE_WASM_URL,
} from "./constants";
import type { NormalizedLandmark } from "./types";

let landmarkerPromise: Promise<FaceLandmarker> | null = null;
let landmarkerInstance: FaceLandmarker | null = null;

export async function getFaceLandmarker(): Promise<FaceLandmarker> {
  if (landmarkerInstance) return landmarkerInstance;

  if (!landmarkerPromise) {
    landmarkerPromise = (async () => {
      const vision = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_URL);

      const create = (delegate: "GPU" | "CPU") =>
        FaceLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: FACE_LANDMARKER_MODEL_URL,
            delegate,
          },
          runningMode: "IMAGE",
          numFaces: 1,
          minFaceDetectionConfidence: 0.5,
          minFacePresenceConfidence: 0.5,
          minTrackingConfidence: 0.5,
        });

      try {
        const landmarker = await create("GPU");
        landmarkerInstance = landmarker;
        return landmarker;
      } catch {
        const landmarker = await create("CPU");
        landmarkerInstance = landmarker;
        return landmarker;
      }
    })().catch((err) => {
      landmarkerPromise = null;
      throw err;
    });
  }

  return landmarkerPromise;
}

export function disposeFaceLandmarker(): void {
  if (landmarkerInstance) {
    landmarkerInstance.close();
    landmarkerInstance = null;
  }
  landmarkerPromise = null;
}

/**
 * Run face mesh on a static image / canvas / video frame.
 * Returns up to 478 landmarks (Face Landmarker) in normalized coords.
 */
export async function detectFaceLandmarks(
  source: HTMLImageElement | HTMLCanvasElement | HTMLVideoElement
): Promise<NormalizedLandmark[] | null> {
  const landmarker = await getFaceLandmarker();
  const result = landmarker.detect(source);
  const face = result.faceLandmarks?.[0];
  if (!face || face.length === 0) return null;

  return face.map((lm) => ({
    x: lm.x,
    y: lm.y,
    z: lm.z,
    visibility: (lm as { visibility?: number }).visibility,
  }));
}
